
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Agency, UserProfile, SequenceTemplate, SequenceStepTemplate, StepType, DocumentCategory, ChecklistTemplate, PipelineType, MetaOAuthConnection, MarketingLeadStats, MarketingLeadStatus, MarketingProvider, GoogleCalendarConnection, RRConnectionStatus } from '../types';
import { storage } from '../services/storage';
import { supabase } from '../lib/supabase';
import { metaService } from '../services/metaService';
import { googleCalendarService } from '../services/googleCalendarService';
import { rentalReadyService } from '../services/rentalReadyService';
import { AdminAuditLogs } from './AdminAuditLogs';
import { SettingsPanel } from './SettingsPanel';
import { getStripePrices } from '../services/stripeService';
import { Building, Users, Plus, Check, Shield, Globe, MapPin, UserPlus, AlertTriangle, X, Edit2, Database, Zap, Trash2, ArrowUp, ArrowDown, FileText, ListChecks, Link as LinkIcon, KeyRound, CheckCircle2, Key, Facebook, Plug2, TrendingUp, Download, BarChart3, Clock, RefreshCw, UserCheck, FileCheck, Mail, Calendar, Filter, XCircle, Settings } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useModal } from './ui/ModalProvider';
import { formatPhoneForStorage, formatPhoneForDisplay, handlePhoneInputChange } from '../lib/phoneUtils';
import { DeleteUserModal } from './modals/DeleteUserModal';

interface AdminPanelProps {
  view: 'users' | 'agencies' | 'sequences' | 'documents' | 'checklists' | 'meta' | 'google-calendar' | 'management-settings' | 'meta-stats' | 'stats' | 'audit-logs' | 'settings' | 'marketing';
  userProfile?: UserProfile;
  selectedAgencyId?: string | null;
}

// UserAgencyDisplay défini en dehors du composant pour éviter la recréation à chaque render
const UserAgencyDisplay = React.memo(({ userId, agencies }: { userId: string; agencies: Agency[] }) => {
    const [userAgencies, setUserAgencies] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => { 
        setIsLoading(true);
        storage.getUserAgencies(userId).then(result => {
            setUserAgencies(result);
            setIsLoading(false);
        }); 
    }, [userId]);
    
    const agencyNames = useMemo(() => {
        if (isLoading) return '';
        return agencies.filter(a => userAgencies.includes(a.id)).map(a => a.city).join(', ');
    }, [agencies, userAgencies, isLoading]);
    
    if (isLoading) {
        return <span className="text-xs text-slate-400 italic">Chargement...</span>;
    }
    
    return <span className="text-xs font-medium text-slate-600">{agencyNames || <span className="italic text-slate-400">Aucune agence</span>}</span>;
}, (prevProps, nextProps) => {
    // Comparaison personnalisée : ne re-render que si userId ou les IDs des agences changent
    if (prevProps.userId !== nextProps.userId) return false;
    
    // Comparer les IDs des agences de manière plus efficace
    if (prevProps.agencies.length !== nextProps.agencies.length) return false;
    
    // Créer un Set pour une comparaison plus rapide
    const prevIds = new Set(prevProps.agencies.map(a => a.id));
    const nextIds = new Set(nextProps.agencies.map(a => a.id));
    
    if (prevIds.size !== nextIds.size) return false;
    
    // Vérifier que tous les IDs sont identiques
    for (const id of prevIds) {
        if (!nextIds.has(id)) return false;
    }
    
    return true; // Props identiques, pas de re-render
});

export const AdminPanel: React.FC<AdminPanelProps> = ({ view, userProfile: propUserProfile, selectedAgencyId }) => {
  const { toast, confirm, alert } = useModal();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [sequences, setSequences] = useState<SequenceTemplate[]>([]);
  const [docCategories, setDocCategories] = useState<DocumentCategory[]>([]);
  const [checklistTemplates, setChecklistTemplates] = useState<ChecklistTemplate[]>([]);
  const [marketingProviders, setMarketingProviders] = useState<MarketingProvider[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Agency Form State
  const [isAgencyModalOpen, setIsAgencyModalOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState<Partial<Agency>>({});
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);

  // RentalReady connection state (agency modal)
  const [rrClientId, setRrClientId] = useState('');
  const [rrClientSecret, setRrClientSecret] = useState('');
  const [rrStatus, setRrStatus] = useState<RRConnectionStatus | null>(null);
  const [rrSaving, setRrSaving] = useState(false);
  const [rrConnecting, setRrConnecting] = useState(false);

  // Charger le statut RentalReady à l'ouverture du modal agence
  useEffect(() => {
    setRrClientId('');
    setRrClientSecret('');
    setRrStatus(null);
    if (isAgencyModalOpen && editingAgency.id) {
      rentalReadyService.getConnectionStatus(editingAgency.id)
        .then(setRrStatus)
        .catch(() => setRrStatus({ configured: false, connected: false, status: 'NOT_CONFIGURED' }));
    }
  }, [isAgencyModalOpen, editingAgency.id]);

  const handleRrSaveCredentials = async () => {
    if (!editingAgency.id || !rrClientId.trim() || !rrClientSecret.trim()) {
      toast.error('Renseignez le Client ID et le Client Secret RentalReady');
      return;
    }
    setRrSaving(true);
    try {
      await rentalReadyService.saveCredentials(editingAgency.id, rrClientId.trim(), rrClientSecret.trim());
      toast.success('Identifiants RentalReady enregistrés');
      setRrClientId('');
      setRrClientSecret('');
      setRrStatus(await rentalReadyService.getConnectionStatus(editingAgency.id));
    } catch (error: any) {
      toast.error('Erreur : ' + (error.message || 'Erreur inconnue'));
    } finally {
      setRrSaving(false);
    }
  };

  const handleRrConnect = async () => {
    if (!editingAgency.id) return;
    setRrConnecting(true);
    try {
      const authUrl = await rentalReadyService.startOAuth(editingAgency.id);
      window.open(authUrl, '_blank', 'noopener');
      toast.success('Autorisez l\'accès dans l\'onglet RentalReady, puis revenez ici');
    } catch (error: any) {
      toast.error('Erreur : ' + (error.message || 'Erreur inconnue'));
    } finally {
      setRrConnecting(false);
    }
  };

  // Sequence Form State
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<Partial<SequenceTemplate>>({
      name: '', description: '', color: 'blue', icon: '⚡', steps: []
  });

  // User Form State
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Doc Categories Form
  const [newDocCategory, setNewDocCategory] = useState('');

  // Checklist Form
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Partial<ChecklistTemplate>>({
      contract_type: PipelineType.CONCIERGE,
      label: '',
      link_url: '',
      link_label: '',
      order_index: 0
  });
  
  const initialUserState = {
      id: '',
      email: '',
      firstName: '',
      lastName: '',
      role: 'user' as 'superadmin' | 'admin' | 'user' | 'prestataire_marketing',
      accessLevel: 'local' as 'national' | 'local',
      agencyIds: [] as string[],
      // Prestataire marketing fields
      marketingProviderId: '' as string,
      isSupervisor: false as boolean,
      marketingAgencyIds: [] as string[]
  };
  const [userForm, setUserForm] = useState(initialUserState);
  const [userSubmitStatus, setUserSubmitStatus] = useState({ loading: false, message: '', error: '' });
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  
  // Marketing Provider Form State
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
  const [providerForm, setProviderForm] = useState<Partial<MarketingProvider>>({ name: '', email: '' });
  
  // Email domain config
  const [emailDomain, setEmailDomain] = useState<string>('@agences-placid.com');

  // Meta OAuth State
  const [metaConnection, setMetaConnection] = useState<MetaOAuthConnection | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [connectingMeta, setConnectingMeta] = useState(false);
  const [metaOAuthProcessing, setMetaOAuthProcessing] = useState(false);
  const [metaOAuthPages, setMetaOAuthPages] = useState<Array<{ id: string; name: string }> | null>(null);
  const [metaSelectedPageId, setMetaSelectedPageId] = useState<string>('');
  const [metaOAuthCode, setMetaOAuthCode] = useState<string>('');
  const [metaOAuthState, setMetaOAuthState] = useState<string>('');
  const [metaOAuthTempToken, setMetaOAuthTempToken] = useState<string>('');
  const [googleConnection, setGoogleConnection] = useState<GoogleCalendarConnection | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleOAuthProcessing, setGoogleOAuthProcessing] = useState(false);
  const [googleReauthRequired, setGoogleReauthRequired] = useState(false);

  // Meta Stats State
  const [metaStats, setMetaStats] = useState<MarketingLeadStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsFilters, setStatsFilters] = useState({
    startDate: '',
    endDate: '',
    campaignId: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setGlobalError(null);
    try {
        const promises: Promise<any>[] = [storage.getAllProfiles()];
        if (view === 'agencies' || view === 'users' || view === 'marketing' || view === 'management-settings') promises.push(storage.getAllAgencies());
        if (view === 'sequences') promises.push(storage.getSequenceTemplates());
        if (view === 'documents') promises.push(storage.getDocumentCategories());
        if (view === 'checklists') promises.push(storage.getChecklistTemplates());
        // Toujours charger les marketing providers (nécessaire pour créer des prestataires marketing)
        promises.push(storage.getMarketingProviders());

        const results = await Promise.all(promises);
        setUsers(results[0]);
        if (view === 'agencies' || view === 'users' || view === 'marketing' || view === 'management-settings') setAgencies(results[1]);
        if (view === 'sequences') setSequences(results[1]);
        if (view === 'documents') setDocCategories(results[1]);
        // Toujours charger les marketing providers (dernier élément du tableau)
        setMarketingProviders(results[results.length - 1] || []);
        if (view === 'checklists') setChecklistTemplates(results[1]);
        
        // Charger le domaine email pour la validation
        const emailDomainConfig = await storage.getSystemConfig('email_domain');
        if (emailDomainConfig) {
            setEmailDomain(emailDomainConfig.domain || '@agences-placid.com');
        }

    } catch (e: any) {
        console.error("Load Data Error:", e);
        if (e?.message?.includes('infinite recursion') || e?.code === '42P17') {
            setGlobalError("ERREUR CRITIQUE SQL : Une boucle infinie a été détectée dans les règles de sécurité Supabase. Veuillez exécuter le script de réparation SQL fourni.");
        }
    } finally {
        setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Charger la connexion Meta si on est sur la vue meta
  useEffect(() => {
    if (view === 'meta') {
      loadMetaConnection();
    }
  }, [view]);

  // Finaliser le flow OAuth après redirection depuis Meta (via Edge Function)
  useEffect(() => {
    if (view !== 'meta') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('meta_oauth') !== '1') return;
    const code = params.get('code') || '';
    const state = params.get('state') || '';
    if (!code) return;

    // Nettoyer l'URL pour éviter les doubles traitements (refresh/back)
    params.delete('meta_oauth');
    params.delete('code');
    params.delete('state');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState(null, '', nextUrl);

    (async () => {
      setMetaOAuthProcessing(true);
      setMetaOAuthPages(null);
      setMetaSelectedPageId('');
      setMetaOAuthCode(code);
      setMetaOAuthState(state);
      setMetaOAuthTempToken('');
      try {
        const res = await metaService.completeOAuth(code, state);
        if ('error' in res) {
          toast.error(`Erreur: ${res.error?.message || 'Impossible de finaliser la connexion Meta'}`);
          return;
        }
        if ('pages' in res && Array.isArray(res.pages)) {
          setMetaOAuthPages(res.pages);
          if (res.pages.length === 1) setMetaSelectedPageId(res.pages[0].id);
          // Stocker le tempToken pour l'utiliser lors de la confirmation
          if (res.tempToken) {
            setMetaOAuthTempToken(res.tempToken);
          }
          toast.info('Sélectionnez la Page Facebook à connecter.');
          return;
        }
        if ('success' in res && res.success) {
          toast.success('Compte Meta connecté avec succès');
          await loadMetaConnection();
          setMetaOAuthPages(null);
          setMetaOAuthCode('');
          setMetaOAuthState('');
          setMetaOAuthTempToken('');
          setMetaSelectedPageId('');
        }
      } catch (e: any) {
        toast.error(`Erreur: ${e.message || 'Erreur inconnue'}`);
      } finally {
        setMetaOAuthProcessing(false);
      }
    })();
  }, [view]);

  // Charger les stats Meta si on est sur la vue meta-stats ou stats
  useEffect(() => {
    if (view === 'meta-stats' || view === 'stats') {
      loadMetaStats();
    }
  }, [view, statsFilters]);

  useEffect(() => {
    if (view === 'google-calendar' || view === 'management-settings') {
      loadGoogleConnection();
    }
  }, [view]);

  useEffect(() => {
    if (view !== 'google-calendar' && view !== 'management-settings') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('gcal_oauth') !== '1') return;
    const code = params.get('code') || '';
    const state = params.get('state') || '';
    if (!code) return;

    params.delete('gcal_oauth');
    params.delete('code');
    params.delete('state');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    window.history.replaceState(null, '', nextUrl);

    (async () => {
      setGoogleOAuthProcessing(true);
      try {
        const res = await googleCalendarService.completeOAuth(code, state);
        if (!res.success) {
          toast.error(`Erreur: ${res.error?.message || 'Impossible de finaliser la connexion Google Agenda'}`);
          return;
        }
        toast.success('Google Agenda connecté avec succès');
        await loadGoogleConnection();
      } catch (e: any) {
        toast.error(`Erreur: ${e.message || 'Erreur inconnue'}`);
      } finally {
        setGoogleOAuthProcessing(false);
      }
    })();
  }, [view]);

  const loadMetaConnection = async () => {
    setMetaLoading(true);
    const connection = await metaService.getConnection();
    setMetaConnection(connection);
    setMetaLoading(false);
  };

  const loadGoogleConnection = async () => {
    setGoogleLoading(true);
    const connection = await googleCalendarService.getConnection();
    setGoogleConnection(connection);
    setGoogleLoading(false);
  };

  const handleConnectMeta = async () => {
    setConnectingMeta(true);
    try {
      const result = await metaService.initiateOAuth();
      if ('error' in result) {
        toast.error(`Erreur: ${result.error?.message || 'Impossible de lancer la connexion Meta'}`);
        setConnectingMeta(false);
        return;
      }
      window.location.href = result.authUrl;
    } catch (error: any) {
      toast.error(`Erreur: ${error.message || 'Erreur inconnue'}`);
      setConnectingMeta(false);
    }
  };

  const handleDisconnectMeta = async () => {
    if (await confirm({
      title: 'Déconnecter Meta',
      message: 'Êtes-vous sûr de vouloir déconnecter le compte Meta ? Les nouveaux leads ne seront plus reçus automatiquement.',
      danger: true
    })) {
      setMetaLoading(true);
      const result = await metaService.disconnect();
      if (result.error) {
        toast.error(`Erreur: ${result.error?.message || 'Impossible de déconnecter'}`);
      } else {
        toast.success('Compte Meta déconnecté avec succès');
        await loadMetaConnection();
      }
      setMetaLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    setGoogleConnecting(true);
    try {
      const result = await googleCalendarService.initiateOAuth();
      if ('error' in result) {
        toast.error(`Erreur: ${result.error?.message || 'Impossible de lancer la connexion Google Agenda'}`);
        return;
      }
      window.location.href = result.authUrl;
    } finally {
      setGoogleConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    confirm({
      title: 'Déconnecter Google Agenda',
      message: 'Êtes-vous sûr de vouloir déconnecter Google Agenda ?',
      confirmText: 'Déconnecter',
      cancelText: 'Annuler',
      variant: 'danger',
      onConfirm: async () => {
        setGoogleLoading(true);
        const result = await googleCalendarService.disconnect();
        if (result.error) {
          toast.error(`Erreur: ${result.error.message}`);
        } else {
          toast.success('Google Agenda déconnecté avec succès');
          await loadGoogleConnection();
        }
        setGoogleLoading(false);
      }
    });
  };

  const handleGoogleResync = async () => {
    setGoogleLoading(true);
    const { data, error } = await googleCalendarService.syncPull();
    if (error) {
      toast.error(`Erreur resync: ${error.message || 'Erreur inconnue'}`);
    } else {
      if (data?.reauthRequired) {
        setGoogleReauthRequired(true);
        toast.error("Reconnexion Google requise (scope Tasks manquant). Cliquez sur Reconnecter.");
      } else {
        setGoogleReauthRequired(false);
        toast.success('Synchronisation Google Agenda terminée');
      }
      await loadGoogleConnection();
    }
    setGoogleLoading(false);
  };

  const handleConfirmMetaPage = async () => {
    if (!metaSelectedPageId) {
      toast.error('Veuillez sélectionner une page.');
      return;
    }
    if (!metaOAuthTempToken) {
      toast.error('Token temporaire manquant. Veuillez relancer la connexion.');
      return;
    }
    setMetaOAuthProcessing(true);
    try {
      // Utiliser le tempToken au lieu du code pour éviter de réutiliser le code
      const res = await metaService.completeOAuthWithToken(metaOAuthTempToken, metaSelectedPageId);
      if ('error' in res) {
        toast.error(`Erreur: ${res.error?.message || 'Impossible d\'enregistrer la connexion Meta'}`);
        return;
      }
      if ('success' in res && res.success) {
        toast.success('Compte Meta connecté avec succès');
        await loadMetaConnection();
        setMetaOAuthPages(null);
        setMetaOAuthCode('');
        setMetaOAuthState('');
        setMetaOAuthTempToken('');
        setMetaSelectedPageId('');
      }
    } finally {
      setMetaOAuthProcessing(false);
    }
  };

  const loadMetaStats = async () => {
    setStatsLoading(true);
    try {
      const filters: any = {};
      if (statsFilters.startDate) filters.startDate = statsFilters.startDate;
      if (statsFilters.endDate) filters.endDate = statsFilters.endDate;
      if (statsFilters.campaignId) filters.campaignId = statsFilters.campaignId;
      const stats = await storage.getMarketingLeadStats(filters);
      setMetaStats(stats);
    } catch (error: any) {
      toast.error('Erreur lors du chargement des statistiques: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setStatsLoading(false);
    }
  };

  const exportStatsToXLS = async () => {
    try {
      const filters: any = { limit: 10000 };
      if (statsFilters.startDate) filters.startDate = statsFilters.startDate;
      if (statsFilters.endDate) filters.endDate = statsFilters.endDate;
      if (statsFilters.campaignId) filters.campaignId = statsFilters.campaignId;
      const leads = await storage.getMarketingLeads(filters);
      
      // Récupérer tous les profils pour mapper les setters
      const profiles = await storage.getAllProfiles();
      const getSetterName = (userId: string | null | undefined): string => {
        if (!userId) return '';
        const profile = profiles.find(p => p.id === userId);
        if (!profile) return '';
        return profile.firstName && profile.lastName
          ? `${profile.firstName} ${profile.lastName}`
          : profile.email || '';
      };

      // Définir les labels de source
      const SOURCE_LABELS: Record<string, string> = {
        meta: 'Meta',
        manual: 'Manuel',
        call: 'Appel',
        other: 'Autre',
        estimation_form: 'Formulaire Estimation',
        leboncoin: 'Leboncoin',
        pap: 'PAP',
        seloger: 'SeLoger'
      };
      
      const getSourceLabel = (source: string): string => {
        return SOURCE_LABELS[source] || source;
      };

      const headers = [
        'Date réception',
        'Nom',
        'Prénom',
        'Email',
        'Téléphone',
        'Adresse',
        'Ville',
        'Code postal',
        'Surface',
        'Pièces',
        'Campagne',
        'Statut',
        'Source',
        'Setter affecté',
        'Motif de rejet'
      ];
      
      const rows = leads.map(lead => [
        new Date(lead.receivedAt).toLocaleString('fr-FR'),
        lead.lastName || '',
        lead.firstName || '',
        lead.email || '',
        lead.phone || '',
        lead.propertyAddress || '',
        lead.propertyCity || '',
        lead.propertyZipCode || '',
        lead.propertySurface?.toString() || '',
        lead.propertyRooms?.toString() || '',
        lead.campaignName || '',
        STATUS_LABELS_META[lead.status].label,
        getSourceLabel(lead.source),
        getSetterName(lead.assignedToUserId),
        lead.rejectionReason || ''
      ]);

      // Créer un workbook Excel
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads Marketing');

      // Générer le fichier Excel
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `marketing-leads-${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Export XLS généré avec succès');
    } catch (error: any) {
      toast.error('Erreur lors de l\'export: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const STATUS_LABELS_META: Record<MarketingLeadStatus, { label: string; color: string; icon: any }> = {
    nouveau: { label: 'Nouveau', color: 'bg-blue-500', icon: Clock },
    en_qualification: { label: 'En qualification', color: 'bg-amber-500', icon: RefreshCw },
    qualifie: { label: 'Qualifié', color: 'bg-green-500', icon: CheckCircle2 },
    transfere: { label: 'Transféré', color: 'bg-purple-500', icon: UserCheck },
    rejete: { label: 'Rejeté', color: 'bg-red-500', icon: X }
  };


  // --- AGENCY LOGIC ---
  const handleSaveAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valider que l'email se termine par le domaine configuré
    if (editingAgency.email && !editingAgency.email.endsWith(emailDomain)) {
        toast.error(`L'email de l'agence doit se terminer par ${emailDomain}. Veuillez corriger l'email.`);
        return;
    }
    
    const agencyToSave = {
      ...editingAgency,
      phone: editingAgency.phone ? formatPhoneForStorage(editingAgency.phone) : ''
    };
    const { error } = await storage.saveAgency(agencyToSave);
    if (error) { 
        toast.error(`Erreur technique: ${error.message}`); 
        return; 
    }
    setIsAgencyModalOpen(false);
    setEditingAgency({});
    loadData();
    toast.success("Agence enregistrée.");
  };
  
  const handleAgencyPhoneBlur = () => {
      if (editingAgency.phone) {
          setEditingAgency({...editingAgency, phone: formatPhoneForStorage(editingAgency.phone)});
      }
  };

  const handleAddressSearch = async (query: string) => {
      setEditingAgency(prev => ({ ...prev, address: query }));
      if (query.length > 3) {
          try {
              const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
              const data = await res.json();
              setAddressSuggestions(data.features || []);
          } catch (e) {
              console.error("Address API Error", e);
          }
      } else {
          setAddressSuggestions([]);
      }
  };

  const selectAddress = (feature: any) => {
      setEditingAgency(prev => ({ 
          ...prev, 
          address: feature.properties.label,
          city: feature.properties.city // Auto-fill city too
      }));
      setAddressSuggestions([]);
  };

  // ... (Code for Sequences, Users, Documents, Checklists remain unchanged - copied verbatim)
  // --- SEQUENCE LOGIC ---
  const handleSaveSequence = async (e: React.FormEvent) => {
      e.preventDefault();
      // Ensure IDs
      const payload: SequenceTemplate = {
          id: editingSequence.id || crypto.randomUUID(),
          name: editingSequence.name!,
          description: editingSequence.description || '',
          color: editingSequence.color || 'blue',
          icon: editingSequence.icon || '⚡',
          steps: editingSequence.steps || []
      };
      
      const { error } = await storage.saveSequenceTemplate(payload);
      if (error) { toast.error("Erreur lors de la sauvegarde."); return; }
      
      setIsSequenceModalOpen(false);
      setEditingSequence({ name: '', description: '', color: 'blue', icon: '⚡', steps: [] });
      loadData();
      toast.success("Modèle sauvegardé.");
  };

  const handleDeleteSequence = async (id: string) => {
      if (await confirm({
          title: "Supprimer le modèle",
          message: "Supprimer ce modèle de séquence définitivement ?",
          danger: true
      })) {
          await storage.deleteSequenceTemplate(id);
          loadData();
          toast.success("Modèle supprimé.");
      }
  };

  const addStepToSequence = () => {
      const newStep: SequenceStepTemplate = { type: 'EMAIL', dayOffset: 1, title: 'Nouvelle étape', content: '' };
      setEditingSequence({ ...editingSequence, steps: [...(editingSequence.steps || []), newStep] });
  };

  const updateStep = (index: number, field: keyof SequenceStepTemplate, value: any) => {
      const newSteps = [...(editingSequence.steps || [])];
      newSteps[index] = { ...newSteps[index], [field]: value };
      setEditingSequence({ ...editingSequence, steps: newSteps });
  };

  const removeStep = (index: number) => {
      const newSteps = [...(editingSequence.steps || [])];
      newSteps.splice(index, 1);
      setEditingSequence({ ...editingSequence, steps: newSteps });
  };

  // --- USER LOGIC ---
  const openCreateUserModal = () => {
      setUserForm(initialUserState);
      setIsEditMode(false);
      setIsUserModalOpen(true);
      setUserSubmitStatus({ loading: false, message: '', error: '' });
  };

  const openEditUserModal = useCallback(async (user: UserProfile) => {
      setIsEditMode(true);
      setUserSubmitStatus({ loading: true, message: '', error: '' }); 
      setIsUserModalOpen(true);

      // Charger les données du prestataire marketing si applicable
      let marketingProviderId = '';
      let isSupervisor = false;
      let marketingAgencyIds: string[] = [];
      let userAgencies: string[] = [];
      
      if (user.role === 'prestataire_marketing') {
          const userProvider = await storage.getUserMarketingProvider(user.id);
          if (userProvider) {
              marketingProviderId = userProvider.id;
          }
          marketingAgencyIds = await storage.getMarketingProviderUserAgencies(user.id);
          // Récupérer isSupervisor depuis la base
          const { data } = await supabase
              .from('marketing_provider_users')
              .select('is_supervisor')
              .eq('user_id', user.id)
              .single();
          if (data) {
              isSupervisor = data.is_supervisor || false;
          }
      } else {
          userAgencies = await storage.getUserAgencies(user.id);
      }
      
      setUserForm({
          id: user.id,
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          role: user.role,
          accessLevel: user.accessLevel,
          agencyIds: userAgencies,
          marketingProviderId,
          isSupervisor,
          marketingAgencyIds
      });
      setUserSubmitStatus({ loading: false, message: '', error: '' });
  }, []);

  const handleUserSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setUserSubmitStatus({ loading: true, message: '', error: '' });

      // Empêcher la création/modification d'utilisateurs avec le rôle superadmin
      if (userForm.role === 'superadmin') {
          setUserSubmitStatus({ 
              loading: false, 
              message: '', 
              error: 'Le rôle Super Admin ne peut être attribué que directement en base de données. Veuillez contacter un administrateur système.' 
          });
          return;
      }

      try {
          if (isEditMode) {
              // Si on modifie un utilisateur qui est superadmin, vérifier qu'on ne change pas son rôle
              const existingUser = users.find(u => u.id === userForm.id);
              if (existingUser?.role === 'superadmin' && userForm.role !== 'superadmin') {
                  setUserSubmitStatus({ 
                      loading: false, 
                      message: '', 
                      error: 'Impossible de modifier le rôle d\'un Super Admin. Le rôle Super Admin ne peut être géré que directement en base de données.' 
                  });
                  return;
              }

              const { error } = await storage.updateProfile(userForm.id, {
                  firstName: userForm.firstName,
                  lastName: userForm.lastName,
                  role: userForm.role,
                  accessLevel: userForm.accessLevel
              });
              if (error) throw error;
              
              // Gestion prestataire marketing
              if (userForm.role === 'prestataire_marketing') {
                  if (!userForm.marketingProviderId) {
                      throw new Error('Veuillez sélectionner un prestataire marketing');
                  }
                  // Validation : un setter doit avoir au moins une agence assignée
                  if (!userForm.isSupervisor && (!userForm.marketingAgencyIds || userForm.marketingAgencyIds.length === 0)) {
                      throw new Error('Veuillez sélectionner au moins une agence pour un setter');
                  }
                  const { error: providerError } = await storage.updateMarketingProviderUser(
                      userForm.id,
                      userForm.isSupervisor,
                      userForm.isSupervisor ? [] : userForm.marketingAgencyIds
                  );
                  if (providerError) throw providerError;
              } else {
                  const { error: agencyError } = await storage.syncUserAgencies(userForm.id, userForm.agencyIds);
                  if (agencyError) throw agencyError;
              }
              
              setUserSubmitStatus({ loading: false, message: 'Utilisateur mis à jour avec succès.', error: '' });
              toast.success("Utilisateur mis à jour.");
          } else {
              // Récupérer le prix depuis Stripe pour afficher dans la confirmation (sauf pour prestataires marketing)
              let priceInfo = '';
              if (userForm.role !== 'superadmin' && userForm.role !== 'prestataire_marketing' && userForm.accessLevel) {
                  try {
                      const { prices } = await getStripePrices();
                      const price = userForm.accessLevel === 'national' ? prices.national : prices.local;
                      if (price && !price.error) {
                          priceInfo = `\n\nL'abonnement sera mis à jour : +${price.amount} ${price.currency}/mois`;
                      }
                  } catch (error) {
                      console.error('Error fetching price:', error);
                  }
              }

              const confirmed = await confirm({
                  title: "Créer l'utilisateur ?",
                  message: `Vous allez créer l'utilisateur ${userForm.firstName} ${userForm.lastName} (${userForm.email}).${priceInfo}\n\nUn email sera envoyé avec un mot de passe temporaire.`,
                  confirmText: "Créer",
                  cancelText: "Annuler"
              });

              if (!confirmed) {
                  setUserSubmitStatus({ loading: false, message: '', error: '' });
                  return;
              }

              const { data: createdUser, error } = await storage.createUser({
                  email: userForm.email,
                  firstName: userForm.firstName,
                  lastName: userForm.lastName,
                  role: userForm.role,
                  accessLevel: userForm.accessLevel,
                  agencyIds: userForm.agencyIds
              });
              if (error) throw error;
              
              // Gestion prestataire marketing
              if (userForm.role === 'prestataire_marketing') {
                  if (!userForm.marketingProviderId) {
                      throw new Error('Veuillez sélectionner un prestataire marketing');
                  }
                  // Validation : un setter doit avoir au moins une agence assignée
                  if (!userForm.isSupervisor && (!userForm.marketingAgencyIds || userForm.marketingAgencyIds.length === 0)) {
                      throw new Error('Veuillez sélectionner au moins une agence pour un setter');
                  }
                  // Utiliser l'ID de l'utilisateur créé directement
                  if (createdUser?.id) {
                      // Attendre un peu pour que le profil soit créé dans la table profiles
                      await new Promise(resolve => setTimeout(resolve, 500));
                      const { error: providerError } = await storage.linkUserToMarketingProvider(
                          userForm.marketingProviderId,
                          createdUser.id,
                          userForm.isSupervisor,
                          userForm.isSupervisor ? [] : userForm.marketingAgencyIds
                      );
                      if (providerError) throw providerError;
                  } else {
                      throw new Error('Impossible de récupérer l\'ID de l\'utilisateur créé');
                  }
              }
              
              setUserSubmitStatus({ loading: false, message: 'Utilisateur créé.', error: '' });
              setUserForm(initialUserState);
              toast.success("Utilisateur créé. Un email a été envoyé.");
          }
          setTimeout(() => { setIsUserModalOpen(false); loadData(); setUserSubmitStatus({ loading: false, message: '', error: '' }); }, 1500);
      } catch (err: any) {
          console.error("User submit error:", err);
          setUserSubmitStatus({ loading: false, message: '', error: err.message || 'Erreur' });
      }
  };

  const handleSendResetPwd = useCallback(async (email: string) => {
      if (await confirm({
          title: "Réinitialiser le mot de passe ?",
          message: `Envoyer un email de réinitialisation à ${email} ?`,
          confirmText: "Envoyer"
      })) {
          const { error } = await storage.sendPasswordReset(email);
          if (error) toast.error("Erreur lors de l'envoi");
          else toast.success("Email envoyé avec succès.");
      }
  }, [confirm, toast]);

  const handleDeleteUser = useCallback((user: UserProfile) => {
      if (user.role === 'superadmin') {
          toast.error('Impossible de supprimer un superadmin');
          return;
      }
      setUserToDelete(user);
  }, [toast]);

  const toggleAgencySelection = (agencyId: string) => {
      setUserForm(prev => {
          const current = prev.agencyIds;
          return current.includes(agencyId) ? { ...prev, agencyIds: current.filter(id => id !== agencyId) } : { ...prev, agencyIds: [...current, agencyId] };
      });
  };


  // --- DOCUMENTS LOGIC ---
  const handleAddDocCategory = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newDocCategory.trim()) return;
      await storage.saveDocumentCategory({ id: crypto.randomUUID(), name: newDocCategory });
      setNewDocCategory('');
      loadData();
      toast.success("Catégorie ajoutée.");
  };

  const handleDeleteDocCategory = async (id: string) => {
      if(await confirm({
          title: "Supprimer la catégorie",
          message: "Confirmez-vous la suppression de cette catégorie de document ?",
          danger: true
      })) {
          await storage.deleteDocumentCategory(id);
          loadData();
          toast.success("Catégorie supprimée.");
      }
  };

  // --- CHECKLISTS LOGIC ---
  const handleSaveChecklist = async (e: React.FormEvent) => {
      e.preventDefault();
      const payload: ChecklistTemplate = {
          id: editingChecklist.id || crypto.randomUUID(),
          contract_type: editingChecklist.contract_type!,
          label: editingChecklist.label!,
          link_url: editingChecklist.link_url,
          link_label: editingChecklist.link_label,
          order_index: editingChecklist.order_index || 0
      };
      const { error } = await storage.saveChecklistTemplate(payload);
      if (error) {
          toast.error(`Erreur lors de la sauvegarde: ${error.message}`);
          return;
      }
      setIsChecklistModalOpen(false);
      setEditingChecklist({ contract_type: PipelineType.CONCIERGE, label: '', link_url: '', link_label: '', order_index: 0 });
      loadData();
      toast.success("Étape enregistrée.");
  };

  const handleDeleteChecklist = async (id: string) => {
      if(await confirm({
          title: "Supprimer l'étape",
          message: "Voulez-vous supprimer cette étape de la checklist ?",
          danger: true
      })) {
          const { error } = await storage.deleteChecklistTemplate(id);
          if (error) {
              toast.error(`Erreur lors de la suppression: ${error.message}`);
              return;
          }
          loadData();
          toast.success("Étape supprimée.");
      }
  };

  // Mémoriser agencies pour éviter les changements de référence
  // Utiliser une comparaison basée sur les IDs pour éviter les recalculs si le contenu est identique
  const agenciesIds = useMemo(() => agencies.map(a => a.id).sort().join(','), [agencies]);
  const memoizedAgencies = useMemo(() => agencies, [agenciesIds]);

  // Mémoriser le tableau des utilisateurs pour éviter les re-renders inutiles
  // Doit être après toutes les définitions de fonctions pour respecter l'ordre des hooks
  const memoizedUsersTable = useMemo(() => {
    if (view !== 'users') return null;
    return users.map(user => (
      <tr key={user.id} className="hover:bg-slate-50">
        <td className="p-4">
          <div className="font-bold text-slate-900">{user.firstName} {user.lastName}</div>
          <div className="text-xs text-slate-500">{user.email}</div>
        </td>
        <td className="p-4">
          <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
            user.role === 'superadmin' ? 'bg-red-100 text-red-700' :
            user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
            user.role === 'prestataire_marketing' ? 'bg-blue-100 text-blue-700' : 
            'bg-slate-100 text-slate-600'
          }`}>
            {user.role === 'superadmin' ? 'Super Admin' :
             user.role === 'admin' ? 'Administrateur' : 
             user.role === 'prestataire_marketing' ? 'Prestataire Marketing' : 
             'Collaborateur'}
          </span>
        </td>
        <td className="p-4">
          {user.role === 'prestataire_marketing' ? (
            <span className="text-xs text-slate-400 italic">—</span>
          ) : (
            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${user.accessLevel === 'national' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
              {user.accessLevel === 'national' ? 'National' : 'Local'}
            </span>
          )}
        </td>
        <td className="p-4">
          {user.role === 'prestataire_marketing' ? (
            // Pour les prestataires marketing, utiliser marketingAgencyIds
            user.isSupervisor ? (
              <span className="text-xs text-slate-400 italic">Toutes</span>
            ) : user.marketingAgencyIds && user.marketingAgencyIds.length > 0 ? (
              <span className="text-xs font-medium text-slate-600">
                {memoizedAgencies
                  .filter(a => user.marketingAgencyIds?.includes(a.id))
                  .map(a => a.city)
                  .join(', ') || <span className="italic text-slate-400">Aucune agence</span>}
              </span>
            ) : (
              <span className="text-xs text-slate-400 italic">Aucune agence</span>
            )
          ) : user.accessLevel === 'national' ? (
            <span className="text-xs text-slate-400 italic">Toutes</span>
          ) : (
            <UserAgencyDisplay userId={user.id} agencies={memoizedAgencies} />
          )}
        </td>
        <td className="p-4 text-right flex justify-end space-x-1">
          {(propUserProfile?.role === 'admin' || propUserProfile?.role === 'superadmin') && (
            <>
              <button onClick={() => openEditUserModal(user)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-slate-100 rounded" title="Modifier">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => handleSendResetPwd(user.email)} className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-slate-100 rounded" title="Réinitialiser mot de passe">
                <KeyRound className="w-4 h-4" />
              </button>
              {user.role !== 'superadmin' && (
                <button onClick={() => handleDeleteUser(user)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded" title="Supprimer">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </td>
      </tr>
    ));
  }, [users, memoizedAgencies, propUserProfile, view, openEditUserModal, handleSendResetPwd, handleDeleteUser]);

  const activeAgency = useMemo(() => {
    if (!agencies.length) return null;
    if (selectedAgencyId) return agencies.find(a => a.id === selectedAgencyId) || null;
    return agencies[0];
  }, [agencies, selectedAgencyId]);

  if (loading) return <div className="p-8 text-center text-slate-500">Chargement de l'administration...</div>;

  return (
    <div className="p-8 h-full overflow-y-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center capitalize">
          {view === 'users' && <Shield className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'agencies' && <Building className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'sequences' && <Zap className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'documents' && <FileText className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'checklists' && <ListChecks className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'meta' && <Facebook className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'google-calendar' && <Calendar className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'management-settings' && <Settings className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'meta-stats' && <BarChart3 className="w-6 h-6 mr-2 text-slate-700" />}
          {view === 'audit-logs' && <FileCheck className="w-6 h-6 mr-2 text-slate-700" />}
          Administration
      </h2>

      {globalError && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded shadow-sm">
              <div className="flex">
                  <AlertTriangle className="h-6 w-6 text-red-500 mr-4" />
                  <div>
                      <h3 className="text-red-800 font-bold">Problème de Configuration</h3>
                      <p className="text-red-700 text-sm mt-1">{globalError}</p>
                  </div>
              </div>
          </div>
      )}

      {/* --- USERS VIEW --- */}
      {view === 'users' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <p className="text-slate-500">Gérez les accès de vos collaborateurs.</p>
                  <button onClick={openCreateUserModal} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg flex items-center shadow-sm font-bold text-sm">
                      <UserPlus className="w-4 h-4 mr-2" /> Nouveau Collaborateur
                  </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                          <tr>
                              <th className="p-4">Utilisateur</th>
                              <th className="p-4">Rôle</th>
                              <th className="p-4">Périmètre</th>
                              <th className="p-4">Agences</th>
                              <th className="p-4 text-right">Actions</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                          {memoizedUsersTable}
                      </tbody>
                  </table>
              </div>
          </div>
      )}

      {/* --- AGENCIES VIEW --- */}
      {view === 'agencies' && (
          <div>
               <div className="flex justify-between items-center mb-6">
                  <p className="text-slate-500">Configurez les entités juridiques (Agences locales).</p>
                  <button onClick={() => { setEditingAgency({ yousignEnvironment: 'sandbox' }); setIsAgencyModalOpen(true); }} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg flex items-center shadow-sm font-bold text-sm">
                      <Plus className="w-4 h-4 mr-2" /> Nouvelle Agence
                  </button>
               </div>
               
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {agencies.map(agency => (
                        <div key={agency.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 relative group hover:border-amber-400 transition-all">
                            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditingAgency({ ...agency, phone: formatPhoneForDisplay(agency.phone || '') }); setIsAgencyModalOpen(true); }} className="p-2 text-slate-400 hover:text-amber-600 bg-slate-50 hover:bg-amber-50 rounded-full">
                                    <Edit2 className="w-4 h-4" />
                                </button>
                            </div>
                            
                            <div className="flex items-center mb-4">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 mr-3">
                                    <Building className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900">{agency.city}</h3>
                                    <p className="text-xs text-slate-500">{agency.name}</p>
                                </div>
                            </div>
                            
                            <div className="space-y-2 text-sm text-slate-600">
                                <div className="flex items-start">
                                    <MapPin className="w-4 h-4 mr-2 mt-0.5 text-slate-400" />
                                    <span className="text-xs">{agency.address}</span>
                                </div>
                                <div className="bg-slate-50 p-2 rounded text-xs mt-3 space-y-1">
                                    <p><span className="font-bold text-slate-700">SIRET:</span> {agency.siret}</p>
                                    <p><span className="font-bold text-slate-700">Structure:</span> {agency.legalStructure}</p>
                                    {agency.capital && <p><span className="font-bold text-slate-700">Capital:</span> {agency.capital} €</p>}
                                    <p><span className="font-bold text-slate-700">RCS:</span> {agency.rcsCity || agency.city}</p>
                                    <div className="flex items-center justify-between pt-1 border-t border-slate-200 mt-1">
                                        <div className="flex items-center text-amber-700">
                                            <Key className="w-3 h-3 mr-1" />
                                            {agency.yousignApiKey ? 'API Configurée' : 'API Manquante'}
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${agency.yousignEnvironment === 'live' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                            {agency.yousignEnvironment === 'live' ? 'LIVE' : 'SANDBOX'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
               </div>
          </div>
      )}

      {/* --- SEQUENCES VIEW --- */}
      {view === 'sequences' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <p className="text-slate-500">Créez des séquences automatiques de relance pour vos prospects.</p>
                  <button onClick={() => { setEditingSequence({ name: '', description: '', color: 'blue', icon: '⚡', steps: [] }); setIsSequenceModalOpen(true); }} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center text-sm font-bold">
                      <Plus className="w-4 h-4 mr-2" /> Nouvelle Séquence
                  </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sequences.map(sequence => (
                      <div key={sequence.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 relative group hover:border-amber-400 transition-all">
                          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                              <button onClick={() => { setEditingSequence(sequence); setIsSequenceModalOpen(true); }} className="p-2 text-slate-400 hover:text-amber-600 bg-slate-50 hover:bg-amber-50 rounded-full">
                                  <Edit2 className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeleteSequence(sequence.id)} className="p-2 text-slate-400 hover:text-red-600 bg-slate-50 hover:bg-red-50 rounded-full">
                                  <Trash2 className="w-4 h-4" />
                              </button>
                          </div>
                          
                          <div className="flex items-center mb-4">
                              <div className={`w-12 h-12 rounded-full bg-${sequence.color}-100 text-${sequence.color}-600 flex items-center justify-center mr-3 text-xl`}>
                                  {sequence.icon}
                              </div>
                              <div className="flex-1">
                                  <h3 className="font-bold text-slate-900">{sequence.name}</h3>
                                  <p className="text-xs text-slate-500">{sequence.description || 'Aucune description'}</p>
                              </div>
                          </div>
                          
                          <div className="space-y-2">
                              <div className="flex items-center justify-between text-xs text-slate-600">
                                  <span className="font-medium">Étapes</span>
                                  <span className="bg-slate-100 px-2 py-0.5 rounded font-bold">{sequence.steps.length}</span>
                              </div>
                              {sequence.steps.length > 0 && (
                                  <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                                      {sequence.steps.slice(0, 3).map((step, idx) => (
                                          <div key={idx} className="text-xs bg-slate-50 p-2 rounded border border-slate-100">
                                              <div className="flex items-center justify-between">
                                                  <span className="font-medium text-slate-700">{step.title}</span>
                                                  <span className="text-slate-400">J+{step.dayOffset}</span>
                                              </div>
                                              <span className="text-[10px] text-slate-500 uppercase">{step.type}</span>
                                          </div>
                                      ))}
                                      {sequence.steps.length > 3 && (
                                          <p className="text-xs text-slate-400 italic text-center">+{sequence.steps.length - 3} autres étapes</p>
                                      )}
                                  </div>
                              )}
                          </div>
                      </div>
                  ))}
                  {sequences.length === 0 && (
                      <div className="col-span-full text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                          <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Aucune séquence créée</p>
                          <p className="text-sm text-slate-400 mt-1">Créez votre première séquence automatique</p>
                      </div>
                  )}
              </div>
          </div>
      )}

      {/* --- DOCUMENTS VIEW --- */}
      {view === 'documents' && (
          <div className="max-w-2xl">
              <p className="text-slate-500 mb-6">Définissez les types de documents acceptés dans les fiches biens (ex: Mandat, DPE, etc.).</p>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
                  <h3 className="font-bold text-slate-800 mb-4">Ajouter une catégorie</h3>
                  <form onSubmit={handleAddDocCategory} className="flex gap-2">
                      <input 
                        type="text" 
                        className="flex-1 p-2 border border-slate-300 rounded-lg bg-white text-slate-900"
                        placeholder="Nom de la catégorie (ex: Assurance PNO)"
                        value={newDocCategory}
                        onChange={(e) => setNewDocCategory(e.target.value)}
                        required
                      />
                      <button type="submit" className="bg-slate-900 text-white px-4 py-2 rounded-lg font-bold flex items-center">
                          <Plus className="w-4 h-4 mr-2" /> Ajouter
                      </button>
                  </form>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                          <tr><th className="p-4">Nom</th><th className="p-4 text-right">Actions</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                          {docCategories.map(cat => (
                              <tr key={cat.id} className="hover:bg-slate-50">
                                  <td className="p-4 font-medium text-slate-800">{cat.name}</td>
                                  <td className="p-4 text-right">
                                      <button onClick={() => handleDeleteDocCategory(cat.id)} className="text-slate-400 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                                  </td>
                              </tr>
                          ))}
                          {docCategories.length === 0 && <tr><td colSpan={2} className="p-4 text-center text-slate-400 italic">Aucune catégorie définie.</td></tr>}
                      </tbody>
                  </table>
              </div>
          </div>
      )}

      {/* --- CHECKLISTS VIEW --- */}
      {view === 'checklists' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <p className="text-slate-500">Personnalisez les étapes d'onboarding pour chaque type de contrat.</p>
                  <button onClick={() => { setEditingChecklist({ contract_type: PipelineType.CONCIERGE, label: '', link_url: '', link_label: '', order_index: checklistTemplates.length }); setIsChecklistModalOpen(true); }} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center text-sm font-bold">
                      <Plus className="w-4 h-4 mr-2" /> Ajouter une étape
                  </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Conciergerie Column */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                      <div className="bg-blue-50 p-4 border-b border-blue-100 flex items-center">
                          <Building className="w-5 h-5 text-blue-600 mr-2" />
                          <h3 className="font-bold text-blue-900">Checklist Conciergerie</h3>
                      </div>
                      <div className="p-4 space-y-2 flex-1">
                          {checklistTemplates.filter(t => t.contract_type === PipelineType.CONCIERGE).length === 0 ? (
                              <div className="text-center py-8 text-slate-400 italic text-sm">
                                  Aucune étape configurée
                              </div>
                          ) : (
                              checklistTemplates.filter(t => t.contract_type === PipelineType.CONCIERGE).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).map(item => (
                                  <div key={item.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-slate-300 transition-colors group bg-white">
                                      <div>
                                          <p className="font-medium text-slate-800 text-sm">{item.label}</p>
                                          {item.link_url && (
                                              <div className="flex items-center mt-1 text-xs text-green-600">
                                                  <LinkIcon className="w-3 h-3 mr-1" />
                                                  Offre : {item.link_label || 'Lien partenaire'}
                                              </div>
                                          )}
                                      </div>
                                      <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button onClick={() => { setEditingChecklist(item); setIsChecklistModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-amber-500"><Edit2 className="w-3.5 h-3.5" /></button>
                                          <button onClick={() => handleDeleteChecklist(item.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                      </div>
                                  </div>
                              ))
                          )}
                      </div>
                  </div>

                  {/* Sublet Column */}
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                      <div className="bg-purple-50 p-4 border-b border-purple-100 flex items-center">
                          <Users className="w-5 h-5 text-purple-600 mr-2" />
                          <h3 className="font-bold text-purple-900">Checklist Sous-location</h3>
                      </div>
                      <div className="p-4 space-y-2 flex-1">
                          {checklistTemplates.filter(t => t.contract_type === PipelineType.SUBLET).length === 0 ? (
                              <div className="text-center py-8 text-slate-400 italic text-sm">
                                  Aucune étape configurée
                              </div>
                          ) : (
                              checklistTemplates.filter(t => t.contract_type === PipelineType.SUBLET).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)).map(item => (
                                  <div key={item.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-slate-300 transition-colors group bg-white">
                                      <div>
                                          <p className="font-medium text-slate-800 text-sm">{item.label}</p>
                                          {item.link_url && (
                                              <div className="flex items-center mt-1 text-xs text-green-600">
                                                  <LinkIcon className="w-3 h-3 mr-1" />
                                                  Offre : {item.link_label || 'Lien partenaire'}
                                              </div>
                                          )}
                                      </div>
                                      <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button onClick={() => { setEditingChecklist(item); setIsChecklistModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-amber-500"><Edit2 className="w-3.5 h-3.5" /></button>
                                          <button onClick={() => handleDeleteChecklist(item.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                      </div>
                                  </div>
                              ))
                          )}
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* --- META CONNECTION VIEW --- */}
      {view === 'meta' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <p className="text-slate-500">Connectez votre compte publicitaire Meta pour recevoir les leads automatiquement.</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                  {metaOAuthProcessing && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                          <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-3" />
                          <p className="text-sm text-blue-800 font-medium">Finalisation de la connexion Meta…</p>
                      </div>
                  )}

                  {metaOAuthPages && metaOAuthPages.length > 0 && (
                      <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                              <Facebook className="w-5 h-5 text-blue-600 mr-2" />
                              Sélection de la Page Facebook
                          </h4>
                          <p className="text-sm text-slate-600 mb-4">
                              Choisissez la Page à connecter. Une seule connexion active est possible à la fois.
                          </p>
                          <div className="flex gap-3 items-center">
                              <select
                                  value={metaSelectedPageId}
                                  onChange={(e) => setMetaSelectedPageId(e.target.value)}
                                  className="flex-1 p-2 border border-slate-300 rounded-lg bg-white"
                              >
                                  <option value="">— Sélectionner une page —</option>
                                  {metaOAuthPages.map(p => (
                                      <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                                  ))}
                              </select>
                              <button
                                  onClick={handleConfirmMetaPage}
                                  disabled={!metaSelectedPageId || metaOAuthProcessing}
                                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-bold text-sm"
                              >
                                  Confirmer
                              </button>
                          </div>
                      </div>
                  )}

                  {metaLoading ? (
                      <div className="text-center py-8">
                          <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
                          <p className="text-slate-500">Chargement...</p>
                      </div>
                  ) : metaConnection && metaConnection.isActive ? (
                      <div className="space-y-6">
                          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center">
                                  <CheckCircle2 className="w-6 h-6 text-green-600 mr-3" />
                                  <div>
                                      <h3 className="font-bold text-green-900">Compte Meta connecté</h3>
                                      <p className="text-sm text-green-700">Page: {metaConnection.pageName}</p>
                                  </div>
                              </div>
                              <button
                                  onClick={handleDisconnectMeta}
                                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-sm"
                              >
                                  Déconnecter
                              </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div className="p-4 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-500 uppercase mb-1">Page ID</p>
                                  <p className="font-mono text-sm text-slate-900">{metaConnection.pageId}</p>
                              </div>
                              <div className="p-4 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-500 uppercase mb-1">Dernière mise à jour</p>
                                  <p className="text-sm text-slate-900">{new Date(metaConnection.updatedAt).toLocaleString('fr-FR')}</p>
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="text-center py-12">
                          <Facebook className="w-16 h-16 text-blue-500 mx-auto mb-6" />
                          <h3 className="text-xl font-bold text-slate-900 mb-2">Connexion Meta requise</h3>
                          <p className="text-slate-500 mb-6 max-w-md mx-auto">
                              Connectez votre compte publicitaire Meta pour recevoir automatiquement les leads de vos campagnes Lead Ads.
                          </p>
                          <button
                              onClick={handleConnectMeta}
                              disabled={connectingMeta}
                              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-bold flex items-center mx-auto"
                          >
                              {connectingMeta ? (
                                  <>
                                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                      Connexion en cours...
                                  </>
                              ) : (
                                  <>
                                      <Plug2 className="w-5 h-5 mr-2" />
                                      Connecter Meta
                                  </>
                              )}
                          </button>
                      </div>
                  )}
              </div>
          </div>
      )}

      {/* --- MANAGEMENT SETTINGS VIEW --- */}
      {view === 'management-settings' && (() => {
          const roleLabel = (() => {
            switch (propUserProfile?.role) {
              case 'superadmin': return 'Super admin';
              case 'admin': return 'Admin';
              case 'user': return 'Utilisateur';
              case 'prestataire_marketing': return 'Prestataire marketing';
              default: return '-';
            }
          })();
          const accessLevelLabel = propUserProfile?.accessLevel === 'national'
            ? 'National'
            : propUserProfile?.accessLevel === 'local'
              ? 'Agence'
              : '-';
          const fullName = [propUserProfile?.firstName, propUserProfile?.lastName].filter(Boolean).join(' ') || '-';

          const userRows: Array<{ label: string; value: React.ReactNode }> = [
            { label: 'Nom', value: fullName },
            { label: 'Email', value: propUserProfile?.email || '-' },
            { label: 'Rôle', value: roleLabel },
            { label: 'Niveau d\'accès', value: accessLevelLabel },
          ];
          const agencyRows: Array<{ label: string; value: React.ReactNode }> = activeAgency
            ? [
                { label: 'Nom', value: activeAgency.name || '-' },
                { label: 'Ville', value: activeAgency.city || '-' },
                { label: 'Adresse', value: activeAgency.address || '-' },
                { label: 'Email', value: activeAgency.email || '-' },
                { label: 'Téléphone', value: activeAgency.phone || '-' },
                { label: 'SIRET', value: activeAgency.siret || '-' },
              ]
            : [];

          const renderRow = (row: { label: string; value: React.ReactNode }, index: number, total: number) => (
            <div
              key={row.label}
              className={`flex items-center justify-between py-3 ${index < total - 1 ? 'border-b border-slate-100' : ''}`}
            >
              <p className="text-sm text-slate-500">{row.label}</p>
              <p className="text-sm font-medium text-slate-900 text-right">{row.value}</p>
            </div>
          );

          return (
          <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Mon profil</h3>
                  <div>
                      {userRows.map((row, i) => renderRow(row, i, userRows.length))}
                  </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Mon agence</h3>
                  {activeAgency ? (
                    <div>
                        {agencyRows.map((row, i) => renderRow(row, i, agencyRows.length))}
                    </div>
                  ) : (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                      Aucune agence active. Contactez votre administrateur.
                    </div>
                  )}
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Google Agenda</h3>
                  <p className="text-sm text-slate-500 mb-6">Synchronisez vos rappels CRM avec Google Agenda et Google Tasks. La connexion est personnelle à chaque utilisateur.</p>
                  {googleOAuthProcessing && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                          <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-3" />
                          <p className="text-sm text-blue-800 font-medium">Finalisation de la connexion Google Agenda…</p>
                      </div>
                  )}
                  {googleReauthRequired && googleConnection?.isActive && (
                      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                          <div className="flex items-center">
                              <RefreshCw className="w-5 h-5 text-amber-600 mr-3" />
                              <div>
                                  <p className="text-sm font-bold text-amber-900">Reconnexion Google requise</p>
                                  <p className="text-xs text-amber-800">Reconnectez votre compte Google pour activer la synchronisation des tâches sans heure.</p>
                              </div>
                          </div>
                          <button onClick={handleConnectGoogle} disabled={googleConnecting} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-lg font-bold text-sm">
                              Reconnecter
                          </button>
                      </div>
                  )}
                  {googleLoading ? (
                      <div className="text-center py-8">
                          <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
                          <p className="text-slate-500">Chargement...</p>
                      </div>
                  ) : googleConnection && googleConnection.isActive ? (
                      <div className="space-y-6">
                          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center">
                                  <CheckCircle2 className="w-6 h-6 text-green-600 mr-3" />
                                  <div>
                                      <h3 className="font-bold text-green-900">Compte Google connecté</h3>
                                      <p className="text-sm text-green-700">{googleConnection.googleEmail} · {googleConnection.calendarSummary || googleConnection.calendarId}</p>
                                      <p className="text-xs text-green-700/80 mt-1">Rappels avec heure → RDV (Google Agenda). Sans heure → tâches (Google Tasks).</p>
                                  </div>
                              </div>
                              <div className="flex items-center gap-2">
                                  <button onClick={handleGoogleResync} className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-bold text-sm">Resync</button>
                                  <button onClick={handleDisconnectGoogle} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-sm">Déconnecter</button>
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="text-center py-12">
                          <Calendar className="w-16 h-16 text-blue-500 mx-auto mb-6" />
                          <h3 className="text-xl font-bold text-slate-900 mb-2">Aucun compte Google connecté</h3>
                          <p className="text-slate-500 mb-6 max-w-md mx-auto">Connectez votre compte Google pour synchroniser automatiquement vos RDV et tâches CRM.</p>
                          <button onClick={handleConnectGoogle} disabled={googleConnecting} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-bold flex items-center mx-auto">
                              {googleConnecting ? (<><RefreshCw className="w-5 h-5 mr-2 animate-spin" />Connexion en cours...</>) : (<><Plug2 className="w-5 h-5 mr-2" />Connecter mon compte Google</>)}
                          </button>
                      </div>
                  )}
              </div>
          </div>
          );
      })()}

      {/* --- GOOGLE CALENDAR CONNECTION VIEW --- */}
      {view === 'google-calendar' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <p className="text-slate-500">Synchronisez vos rendez-vous CRM bidirectionnellement avec Google Agenda (agenda utilisateur).</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                  {googleOAuthProcessing && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                          <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-3" />
                          <p className="text-sm text-blue-800 font-medium">Finalisation de la connexion Google Agenda…</p>
                      </div>
                  )}

                  {googleLoading ? (
                      <div className="text-center py-8">
                          <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
                          <p className="text-slate-500">Chargement...</p>
                      </div>
                  ) : googleConnection && googleConnection.isActive ? (
                      <div className="space-y-6">
                          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center">
                                  <CheckCircle2 className="w-6 h-6 text-green-600 mr-3" />
                                  <div>
                                      <h3 className="font-bold text-green-900">Google Agenda connecté</h3>
                                      <p className="text-sm text-green-700">{googleConnection.googleEmail} - {googleConnection.calendarSummary || googleConnection.calendarId}</p>
                                  </div>
                              </div>
                              <div className="flex items-center gap-2">
                                  <button
                                      onClick={handleGoogleResync}
                                      className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-bold text-sm"
                                  >
                                      Resync
                                  </button>
                                  <button
                                      onClick={handleDisconnectGoogle}
                                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-sm"
                                  >
                                      Déconnecter
                                  </button>
                              </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div className="p-4 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-500 uppercase mb-1">Calendar ID</p>
                                  <p className="font-mono text-sm text-slate-900">{googleConnection.calendarId}</p>
                              </div>
                              <div className="p-4 bg-slate-50 rounded-lg">
                                  <p className="text-xs text-slate-500 uppercase mb-1">Dernière synchro</p>
                                  <p className="text-sm text-slate-900">{googleConnection.lastSyncedAt ? new Date(googleConnection.lastSyncedAt).toLocaleString('fr-FR') : 'Jamais'}</p>
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="text-center py-12">
                          <Calendar className="w-16 h-16 text-blue-500 mx-auto mb-6" />
                          <h3 className="text-xl font-bold text-slate-900 mb-2">Connexion Google Agenda requise</h3>
                          <p className="text-slate-500 mb-6 max-w-md mx-auto">
                              Connectez votre agenda Google pour synchroniser automatiquement les rendez-vous CRM dans les deux sens.
                          </p>
                          <button
                              onClick={handleConnectGoogle}
                              disabled={googleConnecting}
                              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-bold flex items-center mx-auto"
                          >
                              {googleConnecting ? (
                                  <>
                                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                      Connexion en cours...
                                  </>
                              ) : (
                                  <>
                                      <Plug2 className="w-5 h-5 mr-2" />
                                      Connecter Google Agenda
                                  </>
                              )}
                          </button>
                      </div>
                  )}
              </div>
          </div>
      )}

      {/* --- STATS VIEW (Marketing Leads) --- */}
      {(view === 'meta-stats' || view === 'stats') && (
          <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                  <div>
                      <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                              <BarChart3 className="w-6 h-6 text-amber-600" />
                          </div>
                          Statistiques des Leads Marketing
                      </h2>
                      <p className="text-slate-500 mt-2">Analyse de performance des leads (Meta, manuels, appels...)</p>
                  </div>
                  {metaStats && (
                      <button
                          onClick={exportStatsToXLS}
                          className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg flex items-center gap-2 shadow-sm font-medium transition-colors"
                      >
                          <Download className="w-4 h-4" />
                          Exporter XLS
                      </button>
                  )}
              </div>

              {statsLoading ? (
                  <div className="text-center py-12">
                      <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-4" />
                      <p className="text-slate-500">Chargement des statistiques...</p>
                  </div>
              ) : metaStats ? (
                  <div className="space-y-6">
                      {/* Overview Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-3">
                                  <div className="p-2 bg-blue-100 rounded-lg">
                                      <TrendingUp className="w-5 h-5 text-blue-600" />
                                  </div>
                              </div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 tracking-wide">Total Leads</p>
                              <p className="text-3xl font-bold text-slate-900">{metaStats.totalLeads}</p>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-sm border border-green-200 p-6 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-3">
                                  <div className="p-2 bg-green-100 rounded-lg">
                                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  </div>
                              </div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 tracking-wide">Taux Qualification</p>
                              <p className="text-3xl font-bold text-green-600">{metaStats.qualificationRate.toFixed(1)}%</p>
                          </div>
                          <div className="bg-gradient-to-br from-red-50 to-white rounded-xl shadow-sm border border-red-200 p-6 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-3">
                                  <div className="p-2 bg-red-100 rounded-lg">
                                      <XCircle className="w-5 h-5 text-red-600" />
                                  </div>
                              </div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 tracking-wide">Taux Rejet</p>
                              <p className="text-3xl font-bold text-red-600">{metaStats.rejectionRate.toFixed(1)}%</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm border border-purple-200 p-6 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-3">
                                  <div className="p-2 bg-purple-100 rounded-lg">
                                      <UserCheck className="w-5 h-5 text-purple-600" />
                                  </div>
                              </div>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 tracking-wide">Transférés</p>
                              <p className="text-3xl font-bold text-purple-600">{metaStats.byStatus.transfere || 0}</p>
                          </div>
                      </div>

                      {/* Status Distribution */}
                      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                          <div className="flex items-center gap-2 mb-6">
                              <TrendingUp className="w-5 h-5 text-amber-600" />
                              <h3 className="font-bold text-slate-900">Répartition par statut</h3>
                          </div>
                          <div className="space-y-4">
                              {Object.entries(metaStats.byStatus).map(([status, count]) => {
                                  const percentage = metaStats.totalLeads > 0 ? (Number(count) / metaStats.totalLeads) * 100 : 0;
                                  const statusInfo = STATUS_LABELS_META[status as MarketingLeadStatus];
                                  const Icon = statusInfo.icon;
                                  return (
                                      <div key={status} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                                          <div className={`p-2 rounded-lg ${statusInfo.color.replace('bg-', 'bg-').replace('text-', 'bg-').split(' ')[0]}`}>
                                              <Icon className={`w-5 h-5 ${statusInfo.color.split(' ')[1] || 'text-slate-600'}`} />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                              <div className="flex justify-between items-center mb-2">
                                                  <span className="text-sm font-semibold text-slate-900">{statusInfo.label}</span>
                                                  <span className="text-sm font-bold text-slate-700">{count} <span className="text-slate-500">({percentage.toFixed(1)}%)</span></span>
                                              </div>
                                              <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                                  <div
                                                      className={`h-full rounded-full transition-all duration-500 ${statusInfo.color}`}
                                                      style={{ width: `${percentage}%` }}
                                                  ></div>
                                              </div>
                                          </div>
                                      </div>
                                  );
                              })}
                          </div>
                      </div>

                      {/* Campaign Stats */}
                      {metaStats.byCampaign.length > 0 && (
                          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                              <div className="flex items-center gap-2 mb-6">
                                  <Facebook className="w-5 h-5 text-blue-600" />
                                  <h3 className="font-bold text-slate-900">Par campagne</h3>
                              </div>
                              <div className="space-y-3">
                                  {metaStats.byCampaign.map((campaign) => {
                                      const campaignPercentage = metaStats.totalLeads > 0 ? (campaign.count / metaStats.totalLeads) * 100 : 0;
                                      return (
                                          <div key={campaign.campaignId} className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100 hover:border-blue-200 transition-colors">
                                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                                  <div className="p-2 bg-blue-100 rounded-lg">
                                                      <Facebook className="w-4 h-4 text-blue-600" />
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                      <span className="font-semibold text-slate-900 block truncate">{campaign.campaignName}</span>
                                                      <span className="text-xs text-slate-500">{campaignPercentage.toFixed(1)}% du total</span>
                                                  </div>
                                              </div>
                                              <div className="ml-4">
                                                  <span className="text-lg font-bold text-blue-600">{campaign.count}</span>
                                                  <span className="text-sm text-slate-500 ml-1">leads</span>
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </div>
                      )}

                      {/* Setter Stats */}
                      {metaStats.bySetter.length > 0 && (
                          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                              <div className="flex items-center gap-2 mb-6">
                                  <UserCheck className="w-5 h-5 text-purple-600" />
                                  <h3 className="font-bold text-slate-900">Par setter</h3>
                              </div>
                              <div className="space-y-4">
                                  {metaStats.bySetter.map((setter) => {
                                      const setterPercentage = metaStats.totalLeads > 0 ? (setter.count / metaStats.totalLeads) * 100 : 0;
                                      const totalStatus = Object.values(setter.byStatus).reduce((sum: number, count: number) => sum + count, 0);
                                      return (
                                          <div key={setter.userId} className="p-4 bg-gradient-to-r from-purple-50 to-white rounded-lg border border-purple-100 hover:border-purple-200 transition-colors">
                                              <div className="flex items-center justify-between mb-3">
                                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                                      <div className="p-2 bg-purple-100 rounded-lg">
                                                          <UserCheck className="w-4 h-4 text-purple-600" />
                                                      </div>
                                                      <div className="flex-1 min-w-0">
                                                          <span className="font-semibold text-slate-900 block truncate">{setter.userName}</span>
                                                          <span className="text-xs text-slate-500">{setterPercentage.toFixed(1)}% du total • {setter.count} leads</span>
                                                      </div>
                                                  </div>
                                                  <div className="ml-4 flex items-center gap-4">
                                                      <div className="text-right">
                                                          <div className="text-xs text-slate-500 mb-1">Taux qualification</div>
                                                          <span className="text-sm font-bold text-green-600">{setter.qualificationRate.toFixed(1)}%</span>
                                                      </div>
                                                      <div className="text-right">
                                                          <div className="text-xs text-slate-500 mb-1">Taux rejet</div>
                                                          <span className="text-sm font-bold text-red-600">{setter.rejectionRate.toFixed(1)}%</span>
                                                      </div>
                                                  </div>
                                              </div>
                                              <div className="grid grid-cols-5 gap-2 mt-3">
                                                  {Object.entries(setter.byStatus).map(([status, count]) => {
                                                      const statusInfo = STATUS_LABELS_META[status as MarketingLeadStatus];
                                                      const countNum = count as number;
                                                      const totalStatusNum = totalStatus as number;
                                                      const statusPercentage = totalStatusNum > 0 ? (countNum / totalStatusNum) * 100 : 0;
                                                      if (countNum === 0) return null;
                                                      return (
                                                          <div key={status} className="text-center p-2 bg-slate-50 rounded">
                                                              <div className="text-xs font-semibold text-slate-700 mb-1">{countNum}</div>
                                                              <div className="text-[10px] text-slate-500 truncate" title={statusInfo.label}>
                                                                  {statusInfo.label}
                                                              </div>
                                                          </div>
                                                      );
                                                  })}
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </div>
                      )}

                      {/* Rejection Reasons */}
                      {metaStats.rejectionReasons.length > 0 && (
                          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                              <div className="flex items-center gap-2 mb-6">
                                  <XCircle className="w-5 h-5 text-red-600" />
                                  <h3 className="font-bold text-slate-900">Motifs de rejet</h3>
                              </div>
                              <div className="space-y-3">
                                  {metaStats.rejectionReasons.map((reason, index) => {
                                      const totalRejected = metaStats.rejectionReasons.reduce((sum, r) => sum + r.count, 0);
                                      const reasonPercentage = totalRejected > 0 ? (reason.count / totalRejected) * 100 : 0;
                                      return (
                                          <div key={reason.reason} className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-white rounded-lg border border-red-100 hover:border-red-200 transition-colors">
                                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                                  <div className="p-2 bg-red-100 rounded-lg">
                                                      <XCircle className="w-4 h-4 text-red-600" />
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                      <span className="font-medium text-slate-900 block">{reason.reason}</span>
                                                      <span className="text-xs text-slate-500">{reasonPercentage.toFixed(1)}% des rejets</span>
                                                  </div>
                                              </div>
                                              <div className="ml-4">
                                                  <span className="text-lg font-bold text-red-600">{reason.count}</span>
                                                  <span className="text-sm text-slate-500 ml-1">fois</span>
                                              </div>
                                          </div>
                                      );
                                  })}
                              </div>
                          </div>
                      )}
                  </div>
              ) : (
                  <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-slate-200">
                      <div className="p-4 bg-slate-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                          <BarChart3 className="w-10 h-10 text-slate-400" />
                      </div>
                      <p className="text-slate-500 font-medium">Aucune statistique disponible</p>
                      <p className="text-sm text-slate-400 mt-2">Les statistiques apparaîtront ici une fois que des leads auront été traités</p>
                  </div>
              )}
          </div>
      )}

      {/* Agency Modal */}
      {isAgencyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-in zoom-in-95 overflow-hidden">
                <h3 className="text-lg font-bold p-6 pb-0 text-slate-900">{editingAgency.id ? 'Modifier' : 'Créer'} une Agence</h3>
                <form onSubmit={handleSaveAgency} className="flex flex-col flex-1 min-h-0 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 overflow-y-auto min-h-0">
                        {/* Colonne gauche : infos générales */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Nom de l'agence</label>
                                <input className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500" placeholder="Ex: Agence Principale" value={editingAgency.name || ''} onChange={e => setEditingAgency({...editingAgency, name: e.target.value})} required />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-1">Ville</label>
                                <input className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500" placeholder="Ex: Lyon" value={editingAgency.city || ''} onChange={e => setEditingAgency({...editingAgency, city: e.target.value})} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Structure Juridique</label>
                                    <input className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" placeholder="SASU" value={editingAgency.legalStructure || ''} onChange={e => setEditingAgency({...editingAgency, legalStructure: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Capital Social</label>
                                    <input type="number" className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" placeholder="1000" value={editingAgency.capital || ''} onChange={e => setEditingAgency({...editingAgency, capital: parseFloat(e.target.value)})} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email Contact</label>
                                    <input 
                                        type="email"
                                        className={`w-full p-2 border rounded-lg bg-white text-slate-900 text-sm ${
                                            editingAgency.email && !editingAgency.email.endsWith(emailDomain) 
                                                ? 'border-red-500 focus:ring-red-500' 
                                                : 'border-slate-300 focus:ring-amber-500'
                                        }`} 
                                        placeholder={`exemple${emailDomain}`} 
                                        value={editingAgency.email || ''} 
                                        onChange={e => setEditingAgency({...editingAgency, email: e.target.value})} 
                                    />
                                    {editingAgency.email && !editingAgency.email.endsWith(emailDomain) && (
                                        <p className="text-xs text-red-600 mt-1">L'email doit se terminer par {emailDomain}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Téléphone</label>
                                    <input className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm font-mono" placeholder="06 12 34 56 78" value={editingAgency.phone || ''} onChange={e => setEditingAgency({...editingAgency, phone: handlePhoneInputChange(e.target.value)})} onBlur={handleAgencyPhoneBlur} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">SIRET</label>
                                    <input className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" placeholder="123..." value={editingAgency.siret || ''} onChange={e => setEditingAgency({...editingAgency, siret: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Ville RCS</label>
                                    <input className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" placeholder="Lyon" value={editingAgency.rcsCity || ''} onChange={e => setEditingAgency({...editingAgency, rcsCity: e.target.value})} />
                                </div>
                            </div>
                            <div className="relative">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Adresse Postale</label>
                                <input 
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" 
                                    placeholder="123 rue..." 
                                    value={editingAgency.address || ''} 
                                    onChange={e => handleAddressSearch(e.target.value)} 
                                />
                                {addressSuggestions.length > 0 && (
                                    <ul className="absolute z-10 w-full bg-white border border-slate-200 rounded-md mt-1 shadow-lg max-h-48 overflow-auto bottom-full mb-1">
                                        {addressSuggestions.map((s, i) => (
                                            <li key={i} onClick={() => selectAddress(s)} className="p-2 hover:bg-amber-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900">
                                                <span className="font-bold">{s.properties.name}</span> <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Commission Placid (%)</label>
                                <input 
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm" 
                                    placeholder="25" 
                                    value={editingAgency.placidCommissionRate || ''} 
                                    onChange={e => setEditingAgency({...editingAgency, placidCommissionRate: parseFloat(e.target.value) || undefined})} 
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Pourcentage de commission Placid sur les revenus nets (ex: 25 pour 25%)</p>
                            </div>
                        </div>

                        {/* Colonne droite : paramètres API */}
                        <div className="space-y-4">
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase flex items-center justify-between">
                                    <span className="flex items-center"><Key className="w-3 h-3 mr-1" /> Clé API Yousign (V3)</span>
                                    <div className="flex items-center space-x-1">
                                        <label className="text-[10px] font-bold text-slate-400">ENV:</label>
                                        <select 
                                            value={editingAgency.yousignEnvironment || 'sandbox'}
                                            onChange={e => setEditingAgency({...editingAgency, yousignEnvironment: e.target.value as 'sandbox' | 'live'})}
                                            className="text-[10px] p-1 border rounded bg-white text-slate-900 font-bold uppercase"
                                        >
                                            <option value="sandbox">Sandbox (Test)</option>
                                            <option value="live">Production (Live)</option>
                                        </select>
                                    </div>
                                </label>
                                <input 
                                    type="password"
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm font-mono mt-1" 
                                    placeholder="Copiez la clé API ici..." 
                                    value={editingAgency.yousignApiKey || ''} 
                                    onChange={e => setEditingAgency({...editingAgency, yousignApiKey: e.target.value})} 
                                />
                                <p className="text-[10px] text-slate-400 mt-1">
                                    {editingAgency.yousignEnvironment === 'live' ? "⚠️ MODE PRODUCTION : Les signatures auront une valeur légale." : "✅ MODE SANDBOX : Pour les tests uniquement."}
                                </p>
                            </div>

                            {/* RentalReady (OAuth côté serveur : les secrets ne sont jamais relus ici) */}
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase flex items-center justify-between">
                                    <span className="flex items-center"><Plug2 className="w-3 h-3 mr-1" /> RentalReady (PMS)</span>
                                    {!editingAgency.id ? null : rrStatus === null ? (
                                        <span className="text-[10px] font-bold text-slate-400">CHARGEMENT...</span>
                                    ) : rrStatus.status === 'CONNECTED' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-bold">
                                            <CheckCircle2 className="w-3 h-3" /> CONNECTÉ
                                        </span>
                                    ) : rrStatus.status === 'ERROR' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold">
                                            <XCircle className="w-3 h-3" /> ERREUR
                                        </span>
                                    ) : rrStatus.configured ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold">
                                            CONFIGURÉ
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">
                                            NON CONFIGURÉ
                                        </span>
                                    )}
                                </label>
                                {!editingAgency.id ? (
                                    <p className="text-[10px] text-slate-400 mt-1">Enregistrez d'abord l'agence pour configurer RentalReady.</p>
                                ) : (
                                    <div className="space-y-2 mt-1">
                                        <input
                                            type="text"
                                            className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm font-mono"
                                            placeholder={rrStatus?.configured ? 'Client ID (••• configuré)' : 'Client ID OAuth...'}
                                            value={rrClientId}
                                            onChange={e => setRrClientId(e.target.value)}
                                            autoComplete="off"
                                        />
                                        <input
                                            type="password"
                                            className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm font-mono"
                                            placeholder={rrStatus?.configured ? 'Client Secret (••• configuré)' : 'Client Secret OAuth...'}
                                            value={rrClientSecret}
                                            onChange={e => setRrClientSecret(e.target.value)}
                                            autoComplete="new-password"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={handleRrSaveCredentials}
                                                disabled={rrSaving || !rrClientId.trim() || !rrClientSecret.trim()}
                                                className="flex-1 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {rrSaving ? 'Enregistrement...' : 'Enregistrer les identifiants'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleRrConnect}
                                                disabled={rrConnecting || !rrStatus?.configured}
                                                className="flex-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {rrConnecting ? 'Ouverture...' : 'Connecter RentalReady'}
                                            </button>
                                        </div>
                                        {rrStatus?.status === 'ERROR' && rrStatus.lastError && (
                                            <p className="text-[10px] text-red-500 break-all">{rrStatus.lastError}</p>
                                        )}
                                        <p className="text-[10px] text-slate-400">
                                            Le secret est stocké côté serveur et n'est jamais réaffiché. La connexion ouvre la page d'autorisation RentalReady.
                                        </p>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>

                    <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-slate-200 shrink-0">
                        <button type="button" onClick={() => setIsAgencyModalOpen(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded-lg font-medium">Annuler</button>
                        <button type="submit" className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold shadow-sm">Enregistrer</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {/* Sequence Modal */}
      {isSequenceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">{editingSequence.id ? 'Modifier' : 'Créer'} une Séquence</h3>
                        <p className="text-sm text-slate-500">Définissez les étapes automatiques de relance</p>
                    </div>
                    <button onClick={() => setIsSequenceModalOpen(false)} className="hover:bg-slate-200 rounded-full p-1 transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
                </div>
                
                <div className="p-6 overflow-y-auto flex-1">
                    <form onSubmit={handleSaveSequence} className="space-y-6">
                        {/* Basic Info */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <h4 className="text-sm font-bold text-slate-700 mb-4">Informations générales</h4>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Nom de la séquence</label>
                                    <input 
                                        required
                                        className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none" 
                                        placeholder="Ex: Relance Conciergerie"
                                        value={editingSequence.name || ''} 
                                        onChange={e => setEditingSequence({...editingSequence, name: e.target.value})} 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Description</label>
                                    <input 
                                        className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none" 
                                        placeholder="Description optionnelle"
                                        value={editingSequence.description || ''} 
                                        onChange={e => setEditingSequence({...editingSequence, description: e.target.value})} 
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Couleur</label>
                                    <select 
                                        className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                                        value={editingSequence.color || 'blue'} 
                                        onChange={e => setEditingSequence({...editingSequence, color: e.target.value})}
                                    >
                                        <option value="blue">Bleu</option>
                                        <option value="green">Vert</option>
                                        <option value="purple">Violet</option>
                                        <option value="orange">Orange</option>
                                        <option value="red">Rouge</option>
                                        <option value="amber">Ambre</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Icône (Emoji)</label>
                                    <input 
                                        className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none text-xl" 
                                        placeholder="⚡"
                                        value={editingSequence.icon || '⚡'} 
                                        onChange={e => setEditingSequence({...editingSequence, icon: e.target.value})} 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Steps */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-sm font-bold text-slate-700">Étapes de la séquence</h4>
                                <button type="button" onClick={addStepToSequence} className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg flex items-center text-xs font-bold">
                                    <Plus className="w-3 h-3 mr-1" /> Ajouter une étape
                                </button>
                            </div>
                            
                            {editingSequence.steps && editingSequence.steps.length > 0 ? (
                                <div className="space-y-3">
                                    {editingSequence.steps.map((step, index) => (
                                        <div key={index} className="bg-white p-4 rounded-lg border border-slate-200">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center space-x-2">
                                                    <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded">Étape {index + 1}</span>
                                                    <span className="text-xs text-slate-400">J+{step.dayOffset}</span>
                                                </div>
                                                <button 
                                                    type="button"
                                                    onClick={() => removeStep(index)} 
                                                    className="text-red-400 hover:text-red-600 p-1"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-600 mb-1">Type</label>
                                                    <select 
                                                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm focus:ring-amber-500 focus:border-amber-500 outline-none"
                                                        value={step.type} 
                                                        onChange={e => updateStep(index, 'type', e.target.value as StepType)}
                                                    >
                                                        <option value="EMAIL">Email</option>
                                                        <option value="WHATSAPP">WhatsApp</option>
                                                        <option value="CALL">Appel</option>
                                                        <option value="SMS">SMS</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-600 mb-1">Délai (jours)</label>
                                                    <input 
                                                        type="number" 
                                                        min="0"
                                                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm focus:ring-amber-500 focus:border-amber-500 outline-none"
                                                        value={step.dayOffset} 
                                                        onChange={e => updateStep(index, 'dayOffset', parseInt(e.target.value) || 0)}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="mb-3">
                                                <label className="block text-xs font-bold text-slate-600 mb-1">Titre</label>
                                                <input 
                                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm focus:ring-amber-500 focus:border-amber-500 outline-none"
                                                    placeholder="Ex: Relance email"
                                                    value={step.title} 
                                                    onChange={e => updateStep(index, 'title', e.target.value)}
                                                />
                                            </div>
                                            
                                            <div>
                                                <label className="block text-xs font-bold text-slate-600 mb-1">Contenu</label>
                                                <textarea 
                                                    rows={3}
                                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm focus:ring-amber-500 focus:border-amber-500 outline-none resize-none"
                                                    placeholder="Contenu du message..."
                                                    value={step.content} 
                                                    onChange={e => updateStep(index, 'content', e.target.value)}
                                                />
                                            </div>
                                            
                                            {index > 0 && (
                                                <div className="flex justify-end mt-2 space-x-1">
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            const newSteps = [...(editingSequence.steps || [])];
                                                            [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
                                                            setEditingSequence({ ...editingSequence, steps: newSteps });
                                                        }}
                                                        className="text-xs text-slate-400 hover:text-slate-600 p-1"
                                                        title="Déplacer vers le haut"
                                                    >
                                                        <ArrowUp className="w-3 h-3" />
                                                    </button>
                                                    {index < (editingSequence.steps?.length || 0) - 1 && (
                                                        <button 
                                                            type="button"
                                                            onClick={() => {
                                                                const newSteps = [...(editingSequence.steps || [])];
                                                                [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
                                                                setEditingSequence({ ...editingSequence, steps: newSteps });
                                                            }}
                                                            className="text-xs text-slate-400 hover:text-slate-600 p-1"
                                                            title="Déplacer vers le bas"
                                                        >
                                                            <ArrowDown className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-white rounded-lg border border-dashed border-slate-300">
                                    <Zap className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm text-slate-400">Aucune étape définie</p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
                            <button type="button" onClick={() => setIsSequenceModalOpen(false)} className="px-6 py-3 text-slate-500 hover:bg-slate-200 rounded-xl font-bold transition-colors">Annuler</button>
                            <button type="submit" className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold shadow-lg shadow-amber-500/20 transition-all">
                                {editingSequence.id ? 'Modifier' : 'Créer'} la séquence
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
      )}

      {/* User Modal - (Copied verbatim from existing) */}
      {isUserModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">{isEditMode ? 'Modifier' : 'Nouveau'} Collaborateur</h3>
                        <p className="text-sm text-slate-500">Définissez les accès et le périmètre.</p>
                    </div>
                    <button onClick={() => setIsUserModalOpen(false)} className="hover:bg-slate-200 rounded-full p-1 transition-colors"><X className="w-6 h-6 text-slate-400" /></button>
                </div>
                
                <div className="p-8 overflow-y-auto bg-slate-50/50">
                    <form onSubmit={handleUserSubmit} className="space-y-8">
                        {/* Identity Section */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Identité</h4>
                            <div className="grid grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Prénom</label>
                                    <input required className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Ex: Thomas" value={userForm.firstName} onChange={e => setUserForm({...userForm, firstName: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Nom</label>
                                    <input required className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Ex: Durand" value={userForm.lastName} onChange={e => setUserForm({...userForm, lastName: e.target.value})} />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-bold text-slate-700 mb-1">Email professionnel</label>
                                    <input type="email" required disabled={isEditMode} className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none disabled:bg-slate-100 disabled:text-slate-500" placeholder="thomas@placid.com" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} />
                                </div>
                            </div>
                        </div>
                        
                        {/* Permissions Section */}
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Permissions</h4>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Rôle Système</label>
                                    {userForm.role === 'superadmin' ? (
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                            <p className="text-sm text-amber-800 font-medium mb-1">
                                                ⚠️ Super Admin
                                            </p>
                                            <p className="text-xs text-amber-700">
                                                Ce rôle ne peut être géré que directement en base de données. 
                                                Vous pouvez modifier les autres informations de l'utilisateur, 
                                                mais le rôle doit rester inchangé.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex flex-col bg-slate-100 p-1 rounded-lg gap-1">
                                                <div className="flex gap-1">
                                                    <button type="button" onClick={() => setUserForm({...userForm, role: 'user', accessLevel: 'local', agencyIds: []})} className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${userForm.role === 'user' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Collaborateur</button>
                                                    <button type="button" onClick={() => setUserForm({...userForm, role: 'admin', accessLevel: 'national', agencyIds: []})} className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${userForm.role === 'admin' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Admin</button>
                                                </div>
                                                <button type="button" onClick={() => setUserForm({...userForm, role: 'prestataire_marketing', accessLevel: 'national', agencyIds: []})} className={`w-full py-2 text-sm font-bold rounded-md transition-all ${userForm.role === 'prestataire_marketing' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Prestataire Marketing</button>
                                            </div>
                                            <p className="text-xs text-slate-400 mt-2">
                                                {userForm.role === 'admin' ? "Accès complet à la configuration technique." : 
                                                 userForm.role === 'prestataire_marketing' ? "Accès uniquement à la gestion des leads Meta." : 
                                                 "Accès standard aux dossiers et clients."}
                                            </p>
                                        </>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Périmètre</label>
                                    <div className="flex bg-slate-100 p-1 rounded-lg">
                                        <button 
                                            type="button" 
                                            onClick={() => setUserForm({...userForm, accessLevel: 'local', agencyIds: userForm.role === 'prestataire_marketing' ? [] : userForm.agencyIds})} 
                                            disabled={userForm.role === 'prestataire_marketing'}
                                            className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${
                                                userForm.accessLevel === 'local' && userForm.role !== 'prestataire_marketing' 
                                                    ? 'bg-white text-blue-700 shadow-sm' 
                                                    : 'text-slate-500 hover:text-slate-700'
                                            } ${userForm.role === 'prestataire_marketing' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            Local
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => setUserForm({...userForm, accessLevel: 'national', agencyIds: []})} 
                                            className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${userForm.accessLevel === 'national' ? 'bg-white text-green-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                        >
                                            National
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2">
                                        {userForm.role === 'prestataire_marketing' ? "Les prestataires marketing ont automatiquement un accès national." :
                                         userForm.accessLevel === 'national' ? "Voit toutes les agences." : "Limité aux agences sélectionnées ci-dessous."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Agency Selection (Only if Local and not prestataire_marketing) */}
                        {userForm.accessLevel === 'local' && userForm.role !== 'prestataire_marketing' && (
                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-2">
                                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Agences Assignées</h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {agencies.map(a => {
                                        const isSelected = userForm.agencyIds.includes(a.id);
                                        return (
                                            <div 
                                                key={a.id} 
                                                onClick={() => toggleAgencySelection(a.id)}
                                                className={`cursor-pointer border rounded-lg p-3 flex items-center transition-all ${isSelected ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                                            >
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-amber-500 border-amber-500' : 'bg-white border-slate-300'}`}>
                                                    {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-bold ${isSelected ? 'text-amber-900' : 'text-slate-700'}`}>{a.city}</p>
                                                    <p className="text-xs text-slate-400">{a.name}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {agencies.length === 0 && <p className="text-sm text-red-500 italic">Aucune agence n'a été créée. Veuillez créer des agences d'abord.</p>}
                            </div>
                        )}

                        {/* Marketing Provider Configuration (Only if prestataire_marketing) */}
                        {userForm.role === 'prestataire_marketing' && (
                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-2">
                                <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Configuration Prestataire Marketing</h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Société Prestataire *</label>
                                        <select
                                            required
                                            value={userForm.marketingProviderId}
                                            onChange={(e) => setUserForm({...userForm, marketingProviderId: e.target.value})}
                                            className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                                        >
                                            <option value="">Sélectionner une société...</option>
                                            {marketingProviders.map(provider => (
                                                <option key={provider.id} value={provider.id}>{provider.name}</option>
                                            ))}
                                        </select>
                                        {marketingProviders.length === 0 && (
                                            <p className="text-sm text-amber-600 italic mt-1">Aucune société prestataire. Créez-en une dans la section "Prestataires Marketing".</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Rôle dans la société</label>
                                        <div className="flex bg-slate-100 p-1 rounded-lg">
                                            <button
                                                type="button"
                                                onClick={() => setUserForm({...userForm, isSupervisor: true, marketingAgencyIds: []})}
                                                className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${
                                                    userForm.isSupervisor ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                                }`}
                                            >
                                                Superviseur
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setUserForm({...userForm, isSupervisor: false})}
                                                className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${
                                                    !userForm.isSupervisor ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                                }`}
                                            >
                                                Setter
                                            </button>
                                        </div>
                                        <p className="text-xs text-slate-400 mt-2">
                                            {userForm.isSupervisor 
                                                ? "Le superviseur voit tous les leads de toutes les sociétés et toutes les agences." 
                                                : "Le setter ne peut traiter que les leads des agences qui lui sont assignées."}
                                        </p>
                                    </div>
                                    {!userForm.isSupervisor && (
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Agences Assignées *</label>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                {agencies.map(a => {
                                                    const isSelected = userForm.marketingAgencyIds.includes(a.id);
                                                    return (
                                                        <div
                                                            key={a.id}
                                                            onClick={() => {
                                                                const current = userForm.marketingAgencyIds;
                                                                const newAgencies = current.includes(a.id)
                                                                    ? current.filter(id => id !== a.id)
                                                                    : [...current, a.id];
                                                                setUserForm({...userForm, marketingAgencyIds: newAgencies});
                                                            }}
                                                            className={`cursor-pointer border rounded-lg p-3 flex items-center transition-all ${
                                                                isSelected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${
                                                                isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-slate-300'
                                                            }`}>
                                                                {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                                            </div>
                                                            <div>
                                                                <p className={`text-sm font-bold ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>{a.city}</p>
                                                                <p className="text-xs text-slate-400">{a.name}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {userForm.marketingAgencyIds.length === 0 && (
                                                <p className="text-sm text-red-500 italic mt-2">Veuillez sélectionner au moins une agence pour un setter.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <div className="flex justify-end pt-4 border-t border-slate-200">
                            <button type="button" onClick={() => setIsUserModalOpen(false)} className="px-6 py-3 text-slate-500 hover:bg-slate-200 rounded-xl font-bold mr-4 transition-colors">Annuler</button>
                            <button 
                                type="submit" 
                                disabled={userSubmitStatus.loading} 
                                className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold shadow-lg shadow-slate-900/20 disabled:opacity-70 transition-all flex items-center"
                            >
                                {userSubmitStatus.loading ? 'Traitement...' : 'Enregistrer le profil'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
      )}

      {/* Checklist Modal */}
      {isChecklistModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-slate-900">{editingChecklist.id ? 'Modifier' : 'Ajouter'} une étape</h3>
                    <button onClick={() => setIsChecklistModalOpen(false)} className="hover:bg-slate-200 rounded-full p-1 transition-colors">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>
                <form onSubmit={handleSaveChecklist} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Type de contrat</label>
                        <select 
                            className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                            value={editingChecklist.contract_type || PipelineType.CONCIERGE}
                            onChange={e => setEditingChecklist({...editingChecklist, contract_type: e.target.value as PipelineType})}
                            required
                        >
                            <option value={PipelineType.CONCIERGE}>Conciergerie</option>
                            <option value={PipelineType.SUBLET}>Sous-location</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Label de l'étape</label>
                        <input 
                            className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                            placeholder="Ex: Souscrire une assurance PNO"
                            value={editingChecklist.label || ''}
                            onChange={e => setEditingChecklist({...editingChecklist, label: e.target.value})}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">URL du lien partenaire (optionnel)</label>
                        <input 
                            type="url"
                            className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm"
                            placeholder="https://..."
                            value={editingChecklist.link_url || ''}
                            onChange={e => setEditingChecklist({...editingChecklist, link_url: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Label du bouton (optionnel)</label>
                        <input 
                            className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none text-sm"
                            placeholder="Ex: Souscrire"
                            value={editingChecklist.link_label || ''}
                            onChange={e => setEditingChecklist({...editingChecklist, link_label: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-1">Ordre d'affichage</label>
                        <input 
                            type="number"
                            min="0"
                            className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                            value={editingChecklist.order_index || 0}
                            onChange={e => setEditingChecklist({...editingChecklist, order_index: parseInt(e.target.value) || 0})}
                            required
                        />
                    </div>
                    <div className="flex justify-end space-x-2 mt-6">
                        <button type="button" onClick={() => setIsChecklistModalOpen(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded-lg font-medium">Annuler</button>
                        <button type="submit" className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold shadow-sm">Enregistrer</button>
                    </div>
                </form>
              </div>
          </div>
      )}

      {/* --- AUDIT LOGS VIEW --- */}
      {view === 'audit-logs' && propUserProfile && (
          <AdminAuditLogs userProfile={propUserProfile} />
      )}

      {/* --- SETTINGS VIEW --- */}
      {view === 'settings' && (
          <div>
              <div className="mb-6">
                  <h1 className="text-2xl font-bold text-slate-900">Paramètres Système</h1>
                  <p className="text-slate-500 mt-1">Gérer les configurations globales de l'application</p>
              </div>

              <SettingsPanel />
          </div>
      )}

      {/* --- MARKETING VIEW --- */}
      {view === 'marketing' && (
          <div className="space-y-8">
              {/* Section Prestataires Marketing */}
              <div>
                  <div className="mb-6 flex items-center justify-between">
                      <div>
                          <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                              <TrendingUp className="w-6 h-6 mr-3 text-amber-600" />
                              Prestataires Marketing
                          </h1>
                          <p className="text-slate-500 mt-1">
                              Gérez les sociétés prestataires marketing et leurs employés
                          </p>
                      </div>
                      <button
                          onClick={() => {
                              setProviderForm({ name: '', email: '' });
                              setIsProviderModalOpen(true);
                          }}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                      >
                          <Plus className="w-4 h-4" />
                          Nouvelle société
                      </button>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                      <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Nom</th>
                                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">ID</th>
                                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                              {marketingProviders.map(provider => (
                                  <tr key={provider.id} className="hover:bg-slate-50">
                                      <td className="p-4 font-medium text-slate-900">{provider.name}</td>
                                      <td className="p-4 text-slate-600">{provider.email}</td>
                                      <td className="p-4">
                                          <code className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-1 rounded">
                                              {provider.id}
                                          </code>
                                      </td>
                                      <td className="p-4">
                                          <button
                                              onClick={() => {
                                                  setProviderForm(provider);
                                                  setIsProviderModalOpen(true);
                                              }}
                                              className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-slate-100 rounded"
                                              title="Modifier"
                                          >
                                              <Edit2 className="w-4 h-4" />
                                          </button>
                                      </td>
                                  </tr>
                              ))}
                              {marketingProviders.length === 0 && (
                                  <tr>
                                      <td colSpan={4} className="p-8 text-center text-slate-400">
                                          Aucune société prestataire créée
                                      </td>
                                  </tr>
                              )}
                          </tbody>
                      </table>
                  </div>
              </div>

              {/* Section Newsletter */}
              <div>
                  <div className="mb-6">
                      <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                          <Mail className="w-6 h-6 mr-3 text-amber-600" />
                          Marketing - Newsletter
                      </h1>
                      <p className="text-slate-500 mt-1">
                          Exportez les emails des propriétaires qui ont opté pour recevoir la newsletter Placid
                      </p>
                  </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                  <div className="mb-6">
                      <h2 className="text-lg font-semibold text-slate-900 mb-2">Export des emails newsletter</h2>
                      <p className="text-sm text-slate-600">
                          Téléchargez un fichier CSV contenant les informations des propriétaires ayant opté pour la newsletter Placid.
                          Le fichier inclut : Email, Prénom, Nom, Civilité et Date d'inscription.
                      </p>
                  </div>

                  <button
                      onClick={async () => {
                          try {
                              const owners = await storage.getOwnersWithNewsletterOptin();
                              
                              if (owners.length === 0) {
                                  toast.info('Aucun propriétaire n\'a opté pour la newsletter pour le moment.');
                                  return;
                              }

                              const headers = ['Email', 'Prénom', 'Nom', 'Civilité', 'Date d\'inscription'];
                              const rows = owners.map(owner => [
                                  owner.email || '',
                                  owner.firstName || '',
                                  owner.lastName || '',
                                  owner.civility || '',
                                  owner.created_at ? new Date(owner.created_at).toLocaleDateString('fr-FR') : ''
                              ]);
                              
                              const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
                              const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                              const link = document.createElement('a');
                              const url = URL.createObjectURL(blob);
                              link.setAttribute('href', url);
                              link.setAttribute('download', `newsletter-optin-${new Date().toISOString().split('T')[0]}.csv`);
                              link.style.visibility = 'hidden';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success(`Export CSV généré avec succès (${owners.length} propriétaire${owners.length > 1 ? 's' : ''})`);
                          } catch (error: any) {
                              toast.error('Erreur lors de l\'export: ' + (error.message || 'Erreur inconnue'));
                          }
                      }}
                      className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold shadow-sm flex items-center transition-colors"
                  >
                      <Download className="w-5 h-5 mr-2" />
                      Télécharger les emails (CSV)
                  </button>
              </div>
              </div>
          </div>
      )}

      <DeleteUserModal
          isOpen={!!userToDelete}
          user={userToDelete}
          onClose={() => setUserToDelete(null)}
          onDeleted={() => {
              toast.success('Utilisateur supprimé avec succès');
              loadData();
          }}
      />

      {/* Marketing Provider Modal */}
      {isProviderModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-bold text-slate-900">
                          {providerForm.id ? 'Modifier' : 'Nouvelle'} société prestataire
                      </h3>
                      <button onClick={() => setIsProviderModalOpen(false)} className="hover:bg-slate-200 rounded-full p-1 transition-colors">
                          <X className="w-5 h-5 text-slate-400" />
                      </button>
                  </div>
                  <form
                      onSubmit={async (e) => {
                          e.preventDefault();
                          if (!providerForm.name || !providerForm.email) {
                              toast.error('Veuillez remplir tous les champs');
                              return;
                          }
                          const { data, error } = await storage.saveMarketingProvider(providerForm);
                          if (error) {
                              toast.error('Erreur: ' + (error.message || 'Erreur inconnue'));
                          } else {
                              toast.success(providerForm.id ? 'Société modifiée avec succès' : 'Société créée avec succès');
                              setIsProviderModalOpen(false);
                              setProviderForm({ name: '', email: '' });
                              loadData();
                          }
                      }}
                      className="space-y-4"
                  >
                      <div>
                          <label className="block text-sm font-bold text-slate-700 mb-1">Nom de la société *</label>
                          <input
                              required
                              type="text"
                              value={providerForm.name || ''}
                              onChange={(e) => setProviderForm({...providerForm, name: e.target.value})}
                              className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                              placeholder="Ex: Acme Marketing"
                          />
                      </div>
                      <div>
                          <label className="block text-sm font-bold text-slate-700 mb-1">Email général *</label>
                          <input
                              required
                              type="email"
                              value={providerForm.email || ''}
                              onChange={(e) => setProviderForm({...providerForm, email: e.target.value})}
                              className="w-full p-2.5 border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-amber-500 focus:border-amber-500 outline-none"
                              placeholder="contact@acme-marketing.com"
                          />
                          <p className="text-xs text-slate-400 mt-1">Email utilisé pour les notifications (ex: signature de contrat)</p>
                      </div>
                      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                          <button
                              type="button"
                              onClick={() => setIsProviderModalOpen(false)}
                              className="px-6 py-3 text-slate-500 hover:bg-slate-200 rounded-xl font-bold transition-colors"
                          >
                              Annuler
                          </button>
                          <button
                              type="submit"
                              className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold shadow-lg shadow-amber-500/20 transition-all"
                          >
                              {providerForm.id ? 'Modifier' : 'Créer'}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
};